/**
 * Canvas Transform Hook
 *
 * Manages pan and zoom state for the tree canvas.
 * Handles mouse/wheel interactions for smooth navigation.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface CanvasTransform {
  // Transform state
  x: number; // Pan X offset
  y: number; // Pan Y offset
  scale: number; // Zoom scale (0.5 - 2.0)
}

export interface UseCanvasTransformResult {
  transform: CanvasTransform;

  // Actions
  pan: (deltaX: number, deltaY: number) => void;
  zoom: (delta: number, centerX?: number, centerY?: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  centerView: () => void;
  resetTransform: () => void;
  fitToView: (bounds: { width: number; height: number; minX: number; minY: number }, viewportWidth: number, viewportHeight: number) => void; // Phase 3 Checkpoint 3
  centerOnPoint: (x: number, y: number, viewportWidth: number, viewportHeight: number) => void; // For search centering

  // Mouse event handlers
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => void;
  handleMouseLeave: () => void;
  handleWheel: (e: React.WheelEvent) => void;

  // Touch event handlers for mobile/tablet
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;

  // Ref for attaching wheel event with proper options
  wheelHandlerRef: React.RefObject<(e: WheelEvent) => void>;

  // State
  isDragging: boolean;
  isPinching: boolean;
}

interface UseCanvasTransformOptions {
  minScale?: number;
  maxScale?: number;
  initialTransform?: Partial<CanvasTransform>;
}

const DEFAULT_MIN_SCALE = 0.5;
const DEFAULT_MAX_SCALE = 2.0;
const DEFAULT_TRANSFORM: CanvasTransform = {
  x: 0,
  y: 0,
  scale: 1,
};

/**
 * Hook for managing canvas pan/zoom transform
 */
export function useCanvasTransform(
  options: UseCanvasTransformOptions = {}
): UseCanvasTransformResult {
  const {
    minScale = DEFAULT_MIN_SCALE,
    maxScale = DEFAULT_MAX_SCALE,
    initialTransform = {},
  } = options;

  // Transform state
  const [transform, setTransform] = useState<CanvasTransform>({
    ...DEFAULT_TRANSFORM,
    ...initialTransform,
  });

  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const lastTransformRef = useRef<CanvasTransform>(transform);

  // Touch state for mobile/tablet
  const [isPinching, setIsPinching] = useState(false);
  const touchStartRef = useRef<{
    touches: Array<{ x: number; y: number }>;
    distance?: number;
    center?: { x: number; y: number };
    initialTransform?: CanvasTransform;
    timestamp?: number;
    velocity?: { x: number; y: number };
  } | null>(null);

  // Momentum state
  const momentumRef = useRef<{
    animationId: number | null;
    velocity: { x: number; y: number };
  }>({ animationId: null, velocity: { x: 0, y: 0 } });

  // Ref for wheel handler to enable non-passive event listener
  const wheelHandlerRef = useRef<(e: WheelEvent) => void>(() => {});

  // Update ref when transform changes
  useEffect(() => {
    lastTransformRef.current = transform;
  }, [transform]);

  // Helper: Calculate distance between two touch points
  const getTouchDistance = (touch1: Touch, touch2: Touch): number => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Helper: Calculate center point between two touches
  const getTouchCenter = (touch1: Touch, touch2: Touch): { x: number; y: number } => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  /**
   * Pan the canvas by delta pixels
   */
  const pan = useCallback((deltaX: number, deltaY: number) => {
    setTransform(prev => ({
      ...prev,
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));
  }, []);

  /**
   * Zoom with optional center point
   */
  const zoom = useCallback(
    (delta: number, centerX?: number, centerY?: number) => {
      setTransform(prev => {
        const newScale = Math.max(minScale, Math.min(maxScale, prev.scale + delta));

        // If no center point provided, zoom from canvas center
        if (centerX === undefined || centerY === undefined) {
          return {
            ...prev,
            scale: newScale,
          };
        }

        // Zoom towards/away from specific point
        const scaleDiff = newScale - prev.scale;
        const newX = prev.x - (centerX - prev.x) * (scaleDiff / prev.scale);
        const newY = prev.y - (centerY - prev.y) * (scaleDiff / prev.scale);

        return {
          x: newX,
          y: newY,
          scale: newScale,
        };
      });
    },
    [minScale, maxScale]
  );

  /**
   * Zoom in by fixed increment
   */
  const zoomIn = useCallback(() => {
    zoom(0.1);
  }, [zoom]);

  /**
   * Zoom out by fixed increment
   */
  const zoomOut = useCallback(() => {
    zoom(-0.1);
  }, [zoom]);

  /**
   * Center the view
   */
  const centerView = useCallback(() => {
    setTransform({
      x: 0,
      y: 0,
      scale: 1,
    });
  }, []);

  /**
   * Reset transform to default
   */
  const resetTransform = useCallback(() => {
    setTransform({
      ...DEFAULT_TRANSFORM,
      ...initialTransform,
    });
  }, [initialTransform]);

  /**
   * Fit tree bounds to viewport (Phase 3 Checkpoint 3)
   * Calculates optimal scale and pan to show entire tree
   */
  const fitToView = useCallback((
    bounds: { width: number; height: number; minX: number; minY: number },
    viewportWidth: number,
    viewportHeight: number
  ) => {
    if (bounds.width === 0 || bounds.height === 0 || viewportWidth === 0 || viewportHeight === 0) {
      return; // Invalid dimensions, do nothing
    }

    // Calculate scale to fit both width and height with padding
    const padding = 50; // Padding in pixels
    const scaleX = (viewportWidth - padding * 2) / bounds.width;
    const scaleY = (viewportHeight - padding * 2) / bounds.height;
    const optimalScale = Math.min(scaleX, scaleY, maxScale); // Use smaller scale and respect max

    // Clamp to min/max scale
    const finalScale = Math.max(minScale, Math.min(maxScale, optimalScale));

    // Calculate pan to center the tree
    // Calculate the true center of the tree bounds
    const treeCenterX = bounds.minX + bounds.width / 2;
    const treeCenterY = bounds.minY + bounds.height / 2;

    // Calculate viewport center
    const viewportCenterX = viewportWidth / 2;
    const viewportCenterY = viewportHeight / 2;

    // Calculate the transform needed to place tree center at viewport center
    const newX = viewportCenterX - (treeCenterX * finalScale);
    const newY = viewportCenterY - (treeCenterY * finalScale);

    setTransform({
      x: newX,
      y: newY,
      scale: finalScale,
    });
  }, [minScale, maxScale]);

  /**
   * Center view on a specific point (for search functionality)
   * Pans the view so the given point is in the center of the viewport
   */
  const centerOnPoint = useCallback((
    x: number,
    y: number,
    viewportWidth: number,
    viewportHeight: number
  ) => {
    if (viewportWidth === 0 || viewportHeight === 0) {
      return; // Invalid viewport dimensions
    }

    // Use current scale
    const currentScale = lastTransformRef.current.scale;

    // Calculate the transform needed to center this point
    // The point (x, y) in tree coordinates should appear at the center of the viewport
    const newX = (viewportWidth / 2) - (x * currentScale);
    const newY = (viewportHeight / 2) - (y * currentScale);

    setTransform(prev => ({
      ...prev,
      x: newX,
      y: newY,
    }));
  }, []);

  /**
   * Apply momentum scrolling after touch release
   */
  const applyMomentum = useCallback((initialVelocity: { x: number; y: number }) => {
    // Cancel any existing momentum animation
    if (momentumRef.current.animationId) {
      cancelAnimationFrame(momentumRef.current.animationId);
    }

    const friction = 0.95; // Deceleration factor
    const minVelocity = 0.5; // Minimum velocity before stopping

    momentumRef.current.velocity = { ...initialVelocity };

    const animate = () => {
      const velocity = momentumRef.current.velocity;

      // Apply friction
      velocity.x *= friction;
      velocity.y *= friction;

      // Stop if velocity is too small
      if (Math.abs(velocity.x) < minVelocity && Math.abs(velocity.y) < minVelocity) {
        momentumRef.current.animationId = null;
        return;
      }

      // Apply the velocity to the transform
      setTransform(prev => ({
        ...prev,
        x: prev.x + velocity.x,
        y: prev.y + velocity.y,
      }));

      // Continue animation
      momentumRef.current.animationId = requestAnimationFrame(animate);
    };

    momentumRef.current.animationId = requestAnimationFrame(animate);
  }, []);

  /**
   * Handle mouse down - start dragging
   */
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only drag on left mouse button
    if (e.button !== 0) return;

    // Don't start drag if clicking on a person node
    const target = e.target as HTMLElement;
    if (target.closest('.person-node') || target.closest('[role="button"]')) {
      return;
    }

    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - lastTransformRef.current.x,
      y: e.clientY - lastTransformRef.current.y,
    };
    e.preventDefault();
  }, []);

  /**
   * Handle mouse move - pan if dragging
   */
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !dragStartRef.current) return;

    const newX = e.clientX - dragStartRef.current.x;
    const newY = e.clientY - dragStartRef.current.y;

    setTransform(prev => ({
      ...prev,
      x: newX,
      y: newY,
    }));
  }, [isDragging]);

  /**
   * Handle mouse up - stop dragging
   */
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  /**
   * Handle mouse leave - stop dragging
   */
  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  /**
   * Handle wheel - zoom in/out
   * NOTE: This version is for React synthetic events (still needed for TypeScript compatibility)
   * The actual implementation uses a native event listener with { passive: false }
   */
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      // This fallback won't preventDefault to avoid console warnings
      // The real implementation is in the native listener

      // Get mouse position relative to canvas
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Zoom delta (negative = zoom in, positive = zoom out)
      const delta = -Math.sign(e.deltaY) * 0.1;

      zoom(delta, mouseX - transform.x, mouseY - transform.y);
    },
    [zoom, transform.x, transform.y]
  );

  /**
   * Handle touch start - begin touch interaction
   */
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Don't start touch if touching an interactive element
    const target = e.target as HTMLElement;
    if (
      target.closest('.person-node') ||
      target.closest('.person-node-container') ||
      target.closest('[role="button"]') ||
      target.closest('button') ||
      target.closest('.pointer-events-auto')
    ) {
      return;
    }

    const touches = Array.from(e.touches);

    if (touches.length === 1) {
      // Single finger - start pan
      const touch = touches[0];
      setIsDragging(true);
      touchStartRef.current = {
        touches: [{ x: touch.clientX, y: touch.clientY }],
        initialTransform: { ...lastTransformRef.current },
        timestamp: Date.now(),
        velocity: { x: 0, y: 0 }
      };
      // Don't preventDefault here - let native handler decide
    } else if (touches.length === 2) {
      // Two fingers - start pinch zoom
      setIsDragging(false);
      setIsPinching(true);

      const distance = getTouchDistance(touches[0], touches[1]);
      const center = getTouchCenter(touches[0], touches[1]);

      touchStartRef.current = {
        touches: touches.map(t => ({ x: t.clientX, y: t.clientY })),
        distance,
        center,
        initialTransform: { ...lastTransformRef.current }
      };
      // Don't preventDefault here - let native handler decide
    }
  }, []);

  /**
   * Handle touch move - pan or pinch zoom
   */
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    const touches = Array.from(e.touches);

    if (isDragging && touches.length === 1) {
      // Single finger pan
      const touch = touches[0];
      const startTouch = touchStartRef.current.touches[0];
      const deltaX = touch.clientX - startTouch.x;
      const deltaY = touch.clientY - startTouch.y;

      const newX = (touchStartRef.current.initialTransform?.x || 0) + deltaX;
      const newY = (touchStartRef.current.initialTransform?.y || 0) + deltaY;

      // Calculate velocity for momentum
      const now = Date.now();
      const timeDelta = now - (touchStartRef.current.timestamp || now);
      if (timeDelta > 0) {
        const velocityX = (newX - lastTransformRef.current.x) / (timeDelta / 16); // Normalize to ~60fps
        const velocityY = (newY - lastTransformRef.current.y) / (timeDelta / 16);
        touchStartRef.current.velocity = { x: velocityX, y: velocityY };
        touchStartRef.current.timestamp = now;
      }

      setTransform({
        ...lastTransformRef.current,
        x: newX,
        y: newY,
      });
      // Don't preventDefault here - let native handler decide
    } else if (isPinching && touches.length === 2) {
      // Two finger pinch zoom
      const currentDistance = getTouchDistance(touches[0], touches[1]);
      const initialDistance = touchStartRef.current.distance || 1;
      const currentCenter = getTouchCenter(touches[0], touches[1]);

      // Calculate scale change
      const scaleRatio = currentDistance / initialDistance;
      const initialScale = touchStartRef.current.initialTransform?.scale || 1;
      const newScale = Math.max(minScale, Math.min(maxScale, initialScale * scaleRatio));

      // Calculate pan to keep pinch center stable
      const rect = e.currentTarget.getBoundingClientRect();
      const centerX = currentCenter.x - rect.left;
      const centerY = currentCenter.y - rect.top;

      const scaleDiff = newScale - initialScale;
      const newX = (touchStartRef.current.initialTransform?.x || 0) - (centerX - (touchStartRef.current.initialTransform?.x || 0)) * (scaleDiff / initialScale);
      const newY = (touchStartRef.current.initialTransform?.y || 0) - (centerY - (touchStartRef.current.initialTransform?.y || 0)) * (scaleDiff / initialScale);

      setTransform({
        x: newX,
        y: newY,
        scale: newScale,
      });
      // Don't preventDefault here - let native handler decide
    }
  }, [isDragging, isPinching, minScale, maxScale]);

  /**
   * Handle touch end - stop touch interaction
   */
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const touches = Array.from(e.touches);

    if (touches.length === 0) {
      // All fingers lifted
      if (isDragging && touchStartRef.current?.velocity) {
        // Apply momentum if there's velocity from dragging
        const velocity = touchStartRef.current.velocity;
        if (Math.abs(velocity.x) > 1 || Math.abs(velocity.y) > 1) {
          applyMomentum(velocity);
        }
      }
      setIsDragging(false);
      setIsPinching(false);
      touchStartRef.current = null;
    } else if (touches.length === 1 && isPinching) {
      // Switched from pinch to pan
      const touch = touches[0];
      setIsPinching(false);
      setIsDragging(true);
      touchStartRef.current = {
        touches: [{ x: touch.clientX, y: touch.clientY }],
        initialTransform: { ...lastTransformRef.current },
        timestamp: Date.now(),
        velocity: { x: 0, y: 0 }
      };
    }
  }, [isPinching, isDragging, applyMomentum]);

  // Update the wheel handler ref with current implementation
  useEffect(() => {
    wheelHandlerRef.current = (e: WheelEvent) => {
      e.preventDefault();

      // Get mouse position relative to canvas
      const target = e.currentTarget as HTMLElement;
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Zoom delta (negative = zoom in, positive = zoom out)
      const delta = -Math.sign(e.deltaY) * 0.1;

      // Use the zoom function from the current transform state
      setTransform(prev => {
        const newScale = Math.max(minScale, Math.min(maxScale, prev.scale + delta));

        // Zoom towards/away from mouse point
        const scaleDiff = newScale - prev.scale;
        const newX = prev.x - (mouseX - prev.x) * (scaleDiff / prev.scale);
        const newY = prev.y - (mouseY - prev.y) * (scaleDiff / prev.scale);

        return {
          x: newX,
          y: newY,
          scale: newScale,
        };
      });
    };
  }, [minScale, maxScale]);

  return {
    transform,
    pan,
    zoom,
    zoomIn,
    zoomOut,
    centerView,
    resetTransform,
    fitToView, // Phase 3 Checkpoint 3
    centerOnPoint, // For search centering
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    wheelHandlerRef,
    isDragging,
    isPinching,
  };
}
